import React, { useCallback, useEffect, useState } from 'react';
import Button, { ButtonGroup } from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { R400 } from '@atlaskit/theme/colors';
import { GroupMember, Task, useConfirmationDialogContext } from '@css-xp/apd-common';
import BulkApproveModal from '../modal/bulk-approve-modal';
import BulkDeclineModal from '../modal/bulk-decline-modal';
import ChangeAssigneeModal from '../modal/change-assignee-modal';
import { ChangeAssignee } from '../task-list/actions';

enum BulkActions {
  ChangeAssignee = 'Change assignee',
  Approve = 'Approve',
  Decline = 'Decline',
}

interface Props {
  resetSelectedRows: () => void;
  selectedRows?: Task[];
  refetch?: () => void;
}

export const actionPlaceholder = 'Actions';
export const assignValidationErrorText = 'Bulk selections need to have the same approval group and request type';

export const AdminBulkTaskActions = React.memo(({ selectedRows = [], resetSelectedRows, refetch }: Props) => {
  const { openModal, closeModal } = useConfirmationDialogContext();
  const [bulkActionSelectedValue, setBulkActionSelectedValue] = useState<string | undefined>(undefined);
  const [assignValidationError, setAssignValidationError] = useState<string | undefined>(undefined);
  const [selectedGroupMember, setSelectedGroupMember] = useState<GroupMember>({});

  const resetAll = useCallback(() => {
    setBulkActionSelectedValue(undefined);
    resetSelectedRows();
    setSelectedGroupMember({});
    setAssignValidationError(undefined);
  }, []);

  const areHavingSameGroupAndRequestType = useCallback(
    (rows: Task[]) => {
      const groupId = rows?.[0]?.groupId;
      const requestType = rows?.[0]?.requestType;
      for (let index = 1; index < rows.length; index++) {
        const row = rows[index];
        if (groupId !== row.groupId || requestType !== row.requestType) {
          return false;
        }
      }
      return true;
    },
    [selectedRows]
  );

  const handleApply = useCallback(() => {
    switch (bulkActionSelectedValue) {
      //TODO: need to move this to common place
      case BulkActions.Decline:
        openModal({
          modalContent: <BulkDeclineModal taskList={selectedRows} onComplete={refetch} closeModal={closeModal} />,
        });
        return;
      case BulkActions.Approve:
        openModal({
          modalContent: <BulkApproveModal taskList={selectedRows} onComplete={refetch} closeModal={closeModal} />,
        });
        return;
      case BulkActions.ChangeAssignee:
        openModal({
          modalContent: (
            <ChangeAssigneeModal
              taskList={selectedRows}
              assignee={selectedGroupMember?.userId as string}
              onComplete={refetch}
              closeModal={closeModal}
              assigneeName={selectedGroupMember?.name as string}
            />
          ),
        });
        return;
    }
  }, [bulkActionSelectedValue, selectedRows, selectedGroupMember, refetch, closeModal]);

  useEffect(() => {
    if (!selectedRows.length || bulkActionSelectedValue !== BulkActions.ChangeAssignee) {
      setSelectedGroupMember({});
      if (assignValidationError) {
        setAssignValidationError(undefined);
      }
      return;
    }
    if (!areHavingSameGroupAndRequestType(selectedRows)) {
      setSelectedGroupMember({});
      setAssignValidationError(assignValidationErrorText);
    } else {
      setAssignValidationError(undefined);
    }
  }, [bulkActionSelectedValue, selectedRows]);

  const actions = [BulkActions.ChangeAssignee, BulkActions.Approve, BulkActions.Decline];
  const isDisabled =
    !selectedRows.length ||
    !bulkActionSelectedValue ||
    !!assignValidationError ||
    (bulkActionSelectedValue === BulkActions.ChangeAssignee && !selectedGroupMember?.userId);
  return (
    <>
      <div className="bulk-action-dropdown-container" data-testid="bulk-actions">
        {!!selectedRows.length && <div className="total-items-selected">{selectedRows.length} items selected</div>}
        <div>
          <DropdownMenu
            testId="bulk-action-dropdown"
            trigger={!bulkActionSelectedValue ? actionPlaceholder : bulkActionSelectedValue}
            triggerType="button"
          >
            <DropdownItemGroup>
              {actions.map((item) => (
                <DropdownItem
                  data-testid={`action ${item}`}
                  onClick={() => setBulkActionSelectedValue(item)}
                  key={item}
                >
                  {item}
                </DropdownItem>
              ))}
            </DropdownItemGroup>
          </DropdownMenu>
        </div>
        {bulkActionSelectedValue === BulkActions.ChangeAssignee && !assignValidationError && !!selectedRows.length && (
          <ChangeAssignee
            groupId={selectedRows?.[0]?.groupId as string | undefined}
            requestType={selectedRows?.[0]?.requestType as string | undefined}
            onSelectGroupMember={setSelectedGroupMember}
            selectedGroupMember={selectedGroupMember}
          />
        )}
        {assignValidationError && (
          <div className="error-msg">
            <div className="error-toast-icon">
              <ErrorIcon key={'error'} label="Error" primaryColor={R400} size="medium" />
            </div>
            <div className="error-text">{assignValidationError}</div>
          </div>
        )}
      </div>
      <div>
        <ButtonGroup>
          <Button appearance="primary" onClick={handleApply} isDisabled={isDisabled} testId="apply-btn">
            Apply
          </Button>
          <Button appearance="subtle" onClick={resetAll} testId="cancel-btn">
            Cancel
          </Button>
        </ButtonGroup>
      </div>
    </>
  );
});
